import { atom, getDefaultStore } from "jotai";
import { atomWithStorage } from "jotai/utils";
import toast from "react-hot-toast";
import { postReactivateUser, type UserIdentifyResponse } from "../api/apis";
import i18n from "../i18n/config";
import { generateIdentityCommitment } from "../lib/galxeIdentityProtocol";
import { userPalmIDFromId } from "../lib/utils";

export const devAtom = atomWithStorage<boolean>("devAtom", false);

export const userIdentifyAtom = atomWithStorage<UserIdentifyResponse | null>(
  "user",
  null
);

export const jwtAtom = atom<string>(
  (get) => get(userIdentifyAtom)?.tokens?.accessToken || ""
);

export const identityCommitmentAtom = atomWithStorage<string>(
  "identityCommitment",
  ""
);

export const login = async (userIdentify: UserIdentifyResponse) => {
  const store = getDefaultStore();
  store.set(userIdentifyAtom, userIdentify);

  generateIdentityCommitmentWithIdpInternalNullifier(
    userIdentify.user.idpInternalNullifier
  );
};

export const updateUserIdentify = (
  userIdentify: Partial<UserIdentifyResponse>
) => {
  const store = getDefaultStore();
  store.set(userIdentifyAtom, (old) => {
    if (!old) return null;
    return {
      ...old,
      ...userIdentify,
    };
  });

  if (userIdentify.user && !getIdentityCommitment()) {
    generateIdentityCommitmentWithIdpInternalNullifier(
      userIdentify.user.idpInternalNullifier
    );
  }
};

export const removeUser = async () => {
  const store = getDefaultStore();
  store.set(userIdentifyAtom, null);
  store.set(identityCommitmentAtom, "");
  localStorage.removeItem("user");
};

export const logout = () => {
  const store = getDefaultStore();
  store.set(userIdentifyAtom, null);
  store.set(identityCommitmentAtom, "");
  location.href = "/";
};

export const getIdentityCommitment = () => {
  return (localStorage.getItem("identityCommitment") || "")
    .replace('"', "")
    .replace('"', "");
};

const generateIdentityCommitmentWithIdpInternalNullifier = async (
  idpInternalNullifier: string
) => {
  const identityCommitment = await generateIdentityCommitment(
    userPalmIDFromId(idpInternalNullifier)
  );
  const store = getDefaultStore();
  store.set(identityCommitmentAtom, identityCommitment);
};

export const getIdpInternalNullifier = () => {
  const store = getDefaultStore();
  const userIdentify = store.get(userIdentifyAtom);
  return `0x${userIdentify?.user.idpInternalNullifier}`;
};

export const reactivateUser = async () => {
  try {
    const res = await postReactivateUser();
    updateUserIdentify(res);
  } catch (error) {
    console.log("error", error);
    if (String(error).includes("user not found")) {
      toast.error(i18n.t("common.userNotFound"));
      logout();
    }
  }
};

export const isLoggedIn = () => {
  const store = getDefaultStore();
  const userIdentify = store.get(userIdentifyAtom);
  if (!userIdentify) {
    return false;
  }
  return userIdentify?.user.idpInternalNullifier !== null;
};

export const isDeleted = () => {
  const store = getDefaultStore();
  const userIdentify = store.get(userIdentifyAtom);
  return userIdentify && userIdentify?.user.pendingDeletionAt !== null;
};
